import { useMemo, useState } from 'react'
import {
  Activity,
  Briefcase,
  CalendarDays,
  CalendarX,
  Clock,
  Clock3,
  Timer,
} from 'lucide-react'
import { format, parseISO, startOfMonth } from 'date-fns'

import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { Badge, Button, DatePicker, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption, BadgeVariant } from '@/components/ui'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { useMyAttendance } from '../hooks/useMyAttendance'
import { useMyAttendanceToday } from '../hooks/useAttendance'
import { AttendanceCompanyRow } from './AttendanceCompanyRow'
import { AttendanceTodayTotals } from './AttendanceTodayTotals'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/useAuthStore'
import type { AttendanceStatus } from '../types/attendance.types'
import { canAccessAttendanceSelfService, isAdminRole } from '@/features/attendance/utils/attendanceEligibility'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { TimeCorrectionRequestModal } from './TimeCorrectionRequestModal'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'half-day', label: 'Half day' },
  { value: 'on-leave', label: 'On leave' },
]

const STATUS_VARIANT: Record<AttendanceStatus, BadgeVariant> = {
  present: 'success',
  absent: 'danger',
  late: 'warning',
  'half-day': 'info',
  'on-leave': 'neutral',
}

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  'half-day': 'Half day',
  'on-leave': 'On leave',
}

const LIMIT = 10

function formatHours(minutes: number | undefined): string {
  if (!minutes || minutes <= 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function toIsoDate(d: Date | null): string | undefined {
  return d ? format(d, 'yyyy-MM-dd') : undefined
}

function formatCount(value: number, singular: string, plural = `${singular}s`): string {
  return `${value} ${value === 1 ? singular : plural}`
}

export function MyAttendancePage(): React.ReactElement {
  const { can, isLoading: permissionsLoading } = usePermissions()
  const roleSlug = useAuthStore((s) => s.user?.roleSlug)
  const canRead = can('attendance', 'read_self')
  const canCreate = can('attendance', 'create') && !isAdminRole(roleSlug)
  const showNowSection =
    canAccessAttendanceSelfService({ roleSlug, canRead }) && !permissionsLoading
  const { data: todayData, isLoading: todayLoading } = useMyAttendanceToday({
    enabled: showNowSection,
  })

  const [status, setStatus] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | null>(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [page, setPage] = useState(1)
  const [correctionTarget, setCorrectionTarget] = useState<{
    companyId: string
    attendanceId: string
  } | null>(null)
  const companyFilter = useMyCompanyFilter({ onChange: () => setPage(1) })
  const { companies: myCompanies, selectedCompanyIds, companyIds, companyNameById } = companyFilter

  const todayEntries = useMemo(() => todayData?.data ?? [], [todayData?.data])

  const params = useMemo(
    () => ({
      ...(companyIds ? { companyIds } : {}),
      ...(status ? { status: status as AttendanceStatus } : {}),
      ...(toIsoDate(startDate) ? { startDate: toIsoDate(startDate) } : {}),
      ...(toIsoDate(endDate) ? { endDate: toIsoDate(endDate) } : {}),
      page,
      limit: LIMIT,
    }),
    [companyIds, status, startDate, endDate, page]
  )

  const { data, isLoading, isError, error, refetch } = useMyAttendance(params)
  const records = useMemo(() => data?.data ?? [], [data?.data])
  const canRequestCorrection = can('time_corrections', 'create_self')
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const activeShiftCount = useMemo(
    () =>
      todayEntries.reduce(
        (sum, entry) =>
          sum +
          entry.shifts.filter((shift) => shift.attendance?.isActive).length,
        0,
      ),
    [todayEntries],
  )
  const todayShiftCount = useMemo(
    () => todayEntries.reduce((sum, entry) => sum + entry.shifts.length, 0),
    [todayEntries],
  )
  const filteredWorkedMinutes = useMemo(
    () =>
      records.reduce(
        (sum, record) => sum + (record.totalWorkedMinutes ?? 0),
        0,
      ),
    [records],
  )
  const selectedCompanyLabel =
    selectedCompanyIds.size === 0
      ? 'All companies'
      : formatCount(selectedCompanyIds.size, 'company', 'companies')
  const liveStatusLabel =
    activeShiftCount > 0
      ? `${formatCount(activeShiftCount, 'active shift')} now`
      : 'No active shift'

  const onChangeFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(1)
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Attendance' }]}
      icon={Clock}
      title="My Attendance"
      description="Your attendance records across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedCompanyIds}
              onToggle={companyFilter.toggle}
            />
          }
          actions={
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-[0_12px_28px_rgba(15,23,42,0.16)] dark:bg-gray-100 dark:text-gray-900">
              <Clock3 className="h-3.5 w-3.5" aria-hidden />
              {liveStatusLabel} · {format(new Date(), 'd MMM yyyy, h:mm a')}
            </div>
          }
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div className="w-full sm:w-48">
              <Select
                options={STATUS_OPTIONS}
                value={status}
                onChange={onChangeFilter(setStatus)}
              />
            </div>
            <DatePicker
              value={startDate}
              onChange={onChangeFilter(setStartDate)}
            />
            <DatePicker
              value={endDate}
              onChange={onChangeFilter(setEndDate)}
              maxDate={new Date()}
            />
          </div>
        </WorkspaceToolbar>

        <section className="overflow-hidden rounded-[28px] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(247,249,253,0.98))] shadow-[0_28px_70px_rgba(15,23,42,0.08)] dark:border-gray-700 dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.92),rgba(17,24,39,0.96))]">
          <div className="border-b border-white/70 px-5 py-5 dark:border-gray-700/70 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-400">
                  <Activity className="h-3.5 w-3.5 text-primary" aria-hidden />
                  {activeShiftCount > 0
                    ? formatCount(activeShiftCount, 'active shift')
                    : 'Ready for today'}
                </div>
                <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                  Today&apos;s shift desk
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {format(new Date(), 'EEEE, d MMM yyyy · HH:mm')} across{' '}
                  {formatCount(myCompanies.length, 'company', 'companies')}.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:min-w-[520px]">
                <AttendanceMetric
                  icon={Briefcase}
                  label="Coverage"
                  value={selectedCompanyLabel}
                />
                <AttendanceMetric
                  icon={CalendarDays}
                  label="Today"
                  value={formatCount(todayShiftCount, 'shift')}
                />
                <AttendanceMetric
                  icon={Timer}
                  label="This page"
                  value={formatHours(filteredWorkedMinutes)}
                />
              </div>
            </div>
          </div>

          {showNowSection && myCompanies.length > 0 && (
            <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div className="p-4 sm:p-5">
                {todayLoading ? (
                  <SectionLoader variant="list" rows={2} label="Loading today's attendance" />
                ) : (
                  <div className="space-y-2">
                    {myCompanies.map((c) => {
                      const entry = todayEntries.find(
                        (e) => e.companyId === c._id,
                      ) ?? {
                        companyId: c._id,
                        shifts: [],
                      }
                      return (
                        <AttendanceCompanyRow
                          key={c._id}
                          entry={entry}
                          companyName={c.name}
                          canCreate={canCreate}
                          serverTime={todayData?.serverTime}
                        />
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-white/70 bg-white/48 p-4 dark:border-gray-700/70 dark:bg-gray-900/20 lg:border-l lg:border-t-0 sm:p-5">
                <AttendanceTodayTotals
                  entries={todayEntries}
                  companyNameById={companyNameById}
                  serverTime={todayData?.serverTime}
                />
              </div>
            </div>
          )}
        </section>

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={5} rows={6} label="Loading attendance history" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load your attendance"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : records.length === 0 ? (
          <EmptyState
            icon={CalendarX}
            title="No attendance records"
            description="Try adjusting filters or expanding the date range."
          />
        ) : (
          <DataTable
            className="rounded-[28px] border-white/75 bg-white/98 shadow-[0_28px_70px_rgba(15,23,42,0.08)]"
            tableClassName="w-full table-fixed"
          >
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Date</DataTableHeadCell>
                <DataTableHeadCell>Company</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell align="right">Worked</DataTableHeadCell>
                <DataTableHeadCell align="right">Paid</DataTableHeadCell>
                {canRequestCorrection && (
                  <DataTableHeadCell align="right" className="w-44">
                    <span className="sr-only">Actions</span>
                  </DataTableHeadCell>
                )}
              </tr>
            </DataTableHead>
            <DataTableBody>
              {records.map((r) => {
                const cName = r.companyId ? companyNameById.get(r.companyId) ?? '—' : '—'
                return (
                  <DataTableRow
                    key={r._id}
                    className="bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.98))] transition-transform duration-150 hover:-translate-y-[1px] dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.68),rgba(31,41,55,0.92))]"
                  >
                    <DataTableCell className="px-4 py-5 xl:px-6">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {format(parseISO(r.date), 'EEE, d MMM yyyy')}
                      </span>
                    </DataTableCell>
                    <DataTableCell className="px-4 py-5 xl:px-5">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{cName}</span>
                    </DataTableCell>
                    <DataTableCell className="px-4 py-5 xl:px-5">
                      <div className="inline-flex rounded-2xl border border-white/80 bg-gradient-to-r from-slate-400/16 to-slate-300/4 p-3 dark:border-gray-700 dark:bg-gray-800/80">
                        <Badge variant={STATUS_VARIANT[r.status]}>
                          {STATUS_LABEL[r.status]}
                        </Badge>
                      </div>
                    </DataTableCell>
                    <DataTableCell align="right" className="px-4 py-5 font-medium text-gray-700 dark:text-gray-100 xl:px-5">
                      {formatHours(r.totalWorkedMinutes)}
                    </DataTableCell>
                    <DataTableCell align="right" className="px-4 py-5 font-medium text-gray-700 dark:text-gray-100 xl:px-6">
                      {formatHours(r.paidMinutes)}
                    </DataTableCell>
                    {canRequestCorrection && (
                      <DataTableCell align="right" className="px-4 py-5 xl:px-6">
                        {r.companyId && r.clockIn && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="whitespace-nowrap"
                            onClick={() =>
                              setCorrectionTarget({ companyId: r.companyId!, attendanceId: r._id })
                            }
                          >
                            Request correction
                          </Button>
                        )}
                      </DataTableCell>
                    )}
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          className="rounded-2xl border border-white/70 bg-white/88 px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800/90"
        />
      </div>
      {correctionTarget && (
        <TimeCorrectionRequestModal
          companyId={correctionTarget.companyId}
          attendanceId={correctionTarget.attendanceId}
          open
          onClose={() => setCorrectionTarget(null)}
          onSuccess={() => setCorrectionTarget(null)}
        />
      )}
    </PageShell>
  )
}

interface AttendanceMetricProps {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  label: string
  value: string
}

function AttendanceMetric({
  icon: Icon,
  label,
  value,
}: AttendanceMetricProps): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-[24px] border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,248,252,0.98))] p-4 dark:border-gray-700 dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.86),rgba(17,24,39,0.92))]">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
        {label}
      </div>
      <div className="truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        {value}
      </div>
    </div>
  )
}

MyAttendancePage.displayName = 'MyAttendancePage'
